import type { components } from './schema';

export type SessionUser = components['schemas']['UserInfo'];

/**
 * unknown       - page just loaded, the cookie has not been tried yet
 * authenticated - a valid access token is held in memory
 * anonymous     - nobody is signed in (never was, or logged out)
 * expired       - a session existed and could not be renewed (revoked, expired, password changed)
 */
export type SessionStatus = 'unknown' | 'authenticated' | 'anonymous' | 'expired';

export interface SessionSnapshot {
  status: SessionStatus;
  user: SessionUser | null;
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly problem?: Record<string, unknown>,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

interface LoginBody {
  accessToken: string;
  expiresIn: number;
  user: SessionUser;
}

/** What a sign-in leads to: a session, or a second step for Managers and Admins ("verify" a code, or "enrol" an authenticator first). */
export type LoginOutcome =
  { status: 'signed-in' } | { status: 'two-factor'; kind: 'verify' | 'enrol'; challengeToken: string };

export interface EnrolmentSetup {
  /** The base32 secret, for typing into an authenticator app. */
  secret: string;
  /** The otpauth:// address an authenticator app reads from a QR code. */
  uri: string;
}

export interface SignInOptions {
  demoRegistration: boolean;
  demoDeposit: number;
  supportContact?: string | null;
  /** The server can email a "forgot password" link. */
  passwordReset?: boolean;
}

/** A demo account just opened: its number, to show before the session starts, and `finish` to start it. */
export interface DemoAccount {
  login: number;
  /** Starts the session in this page (the user has seen their account number). */
  finish(): void;
}

export interface Enrolment {
  /** Shown once. */
  recoveryCodes: string[];
  /** Call after the user confirmed they saved the recovery codes: only then does the session start in this page. */
  finish(): void;
}

interface Shared {
  accessToken: string;
  expiresAt: number;
  user: SessionUser;
}

/** The two browser primitives that make refresh safe across tabs; injectable for tests. */
export interface LockManagerLike {
  request<T>(name: string, callback: () => Promise<T>): Promise<T>;
}
export interface ChannelLike {
  postMessage(message: unknown): void;
  onmessage: ((event: MessageEvent) => void) | null;
  close(): void;
}

export interface TokenManagerOptions {
  baseUrl?: string;
  fetch?: typeof fetch;
  now?: () => number;
  locks?: LockManagerLike | null;
  channel?: ChannelLike | null;
  /** Renew this long before the token expires. */
  refreshLeadMs?: number;
  /** Retry delay after a network failure while renewing (the session is kept). */
  retryMs?: number;
}

type Listener = (snapshot: SessionSnapshot) => void;

/**
 * Holds the access token IN MEMORY ONLY (never localStorage, sessionStorage or the URL) and renews it silently.
 *
 * The refresh token is an HttpOnly cookie the browser sends by itself. The server treats a refresh token that is presented
 * twice as theft and signs the whole session family out, so two tabs must never refresh at the same moment: renewal runs
 * under a Web Lock, and the fresh token is broadcast so the other tabs adopt it instead of refreshing again.
 */
export class TokenManager {
  private readonly baseUrl: string;
  private readonly fetchFn: typeof fetch;
  private readonly now: () => number;
  private readonly locks: LockManagerLike | null;
  private readonly channel: ChannelLike | null;
  private readonly leadMs: number;
  private readonly retryMs: number;
  private readonly listeners = new Set<Listener>();
  private token: string | null = null;
  private expiresAt = 0;
  private snapshot: SessionSnapshot = { status: 'unknown', user: null };
  private timer: ReturnType<typeof setTimeout> | null = null;
  private inflight: Promise<boolean> | null = null;

  constructor(options: TokenManagerOptions = {}) {
    this.baseUrl = options.baseUrl ?? '';
    this.fetchFn = options.fetch ?? ((...args) => fetch(...args));
    this.now = options.now ?? Date.now;
    this.locks =
      options.locks !== undefined
        ? options.locks
        : typeof navigator !== 'undefined' && 'locks' in navigator
          ? navigator.locks
          : null;
    this.channel =
      options.channel !== undefined
        ? options.channel
        : typeof BroadcastChannel !== 'undefined'
          ? new BroadcastChannel('adiox-session')
          : null;
    this.leadMs = options.refreshLeadMs ?? 60_000;
    this.retryMs = options.retryMs ?? 10_000;
    if (this.channel) {
      this.channel.onmessage = (event) => this.onBroadcast(event.data);
    }
  }

  get session(): SessionSnapshot {
    return this.snapshot;
  }

  get accessToken(): string | null {
    return this.token;
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /** Signs in by email (staff, and the manager portal). Wrong credentials and lockout both come back as 401 with the same message. Staff get a second step instead of a session. */
  async login(email: string, password: string): Promise<LoginOutcome> {
    return this.performLogin({ email, password });
  }

  /** The terminal's own sign-in: an account number instead of an email, MT5-style. Same rules as {@link login}. */
  async loginByAccount(login: number, password: string): Promise<LoginOutcome> {
    return this.performLogin({ login, password });
  }

  /** Sign-in with an account's investor (read-only) password: a session that can only look at that one account. */
  async loginInvestor(login: number, password: string): Promise<LoginOutcome> {
    return this.performLogin({ login, password }, '/api/v1/auth/login-investor');
  }

  private async performLogin(
    body: { email: string; password: string } | { login: number; password: string },
    path = '/api/v1/auth/login',
  ): Promise<LoginOutcome> {
    const response = await this.post(path, body);
    if (!response.ok) {
      throw await toError(response);
    }
    const json = await response.json();
    if (response.status === 202) {
      return { status: 'two-factor', kind: json.kind, challengeToken: json.challengeToken };
    }
    this.adopt(json, true);
    return { status: 'signed-in' };
  }

  /**
   * What the sign-in screen may offer (demo sign-up, the support contact). Asked without a session, so it does not go
   * through the API client, which would first try to renew one. Null when the server cannot be reached.
   */
  async signInOptions(): Promise<SignInOptions | null> {
    try {
      const response = await this.fetchFn(`${this.baseUrl}/api/v1/auth/options`, {
        credentials: 'same-origin',
      });
      return response.ok ? ((await response.json()) as SignInOptions) : null;
    } catch {
      return null;
    }
  }

  /**
   * Opens a demo account (when the server allows it) and returns its number. The session only starts in this page when
   * `finish` is called, so the new number can be shown first: it is what the user signs in with next time.
   */
  async registerDemo(email: string, password: string): Promise<DemoAccount> {
    const response = await this.post('/api/v1/auth/register-demo', { email, password });
    if (!response.ok) {
      throw await toError(response);
    }
    const body = await response.json();
    let finished = false;
    return {
      login: Number(body.user?.accounts?.[0] ?? 0),
      finish: () => {
        if (!finished) {
          finished = true;
          this.adopt(body, true);
        }
      },
    };
  }

  /**
   * Asks for a "forgot password" email. The server answers the same whether or not the account exists, so this only fails
   * when the request itself does (offline, too many attempts).
   */
  async forgotPassword(who: { email: string } | { login: number }): Promise<void> {
    const response = await this.post('/api/v1/auth/forgot-password', who);
    if (!response.ok) throw await toError(response);
  }

  /** Sets a new password with the token from the emailed link. No session starts: the user signs in with it. */
  async resetPassword(token: string, password: string): Promise<void> {
    const response = await this.post('/api/v1/auth/reset-password', { token, password });
    if (!response.ok) throw await toError(response);
  }

  /** Second step for a user who already has an authenticator: a 6-digit code or a recovery code. */
  async verifyTwoFactor(challengeToken: string, code: string): Promise<void> {
    const response = await this.post('/api/v1/auth/two-factor/verify', { challengeToken, code });
    if (!response.ok) {
      throw await toError(response);
    }
    this.adopt(await response.json(), true);
  }

  /** First step of enrolment: a new secret to put into the authenticator app. */
  async startEnrolment(challengeToken: string): Promise<EnrolmentSetup> {
    const response = await this.post('/api/v1/auth/two-factor/enrol/start', { challengeToken });
    if (!response.ok) {
      throw await toError(response);
    }
    return (await response.json()) as EnrolmentSetup;
  }

  /** Proves the app works. The recovery codes come back once; the session begins when `finish` is called. */
  async confirmEnrolment(challengeToken: string, code: string): Promise<Enrolment> {
    const response = await this.post('/api/v1/auth/two-factor/enrol/confirm', { challengeToken, code });
    if (!response.ok) {
      throw await toError(response);
    }
    const body = await response.json();
    let finished = false;
    return {
      recoveryCodes: body.recoveryCodes as string[],
      finish: () => {
        if (!finished) {
          finished = true;
          this.adopt(body, true);
        }
      },
    };
  }

  /** Tries the refresh cookie once, e.g. at page load. Resolves true when a session was restored. */
  async restore(): Promise<boolean> {
    const ok = await this.refresh();
    if (!ok && this.snapshot.status === 'unknown') {
      this.setSnapshot({ status: 'anonymous', user: null });
    }
    return ok;
  }

  /**
   * Renews the access token (single flight, cross-tab safe). False when the session is gone.
   * Pass the token the server just rejected (a 401): the renewal then really contacts the server, unless another tab has
   * already replaced that token.
   */
  refresh(rejected?: string | null): Promise<boolean> {
    this.inflight ??= this.runRefresh(rejected).finally(() => {
      this.inflight = null;
    });
    return this.inflight;
  }

  /** A token that is valid right now, renewing it first when it is about to expire. Null when signed out. */
  async getToken(): Promise<string | null> {
    if (this.token && this.expiresAt - this.now() > 15_000) {
      return this.token;
    }
    if (this.snapshot.status === 'anonymous' || this.snapshot.status === 'expired') {
      return null;
    }
    await this.refresh();
    return this.token;
  }

  async logout(): Promise<void> {
    try {
      await this.post('/api/v1/auth/logout', undefined);
    } catch {
      // the local session ends either way
    }
    this.clear('anonymous');
  }

  /** The server rejected our token and renewal did not help: the session is over. */
  markExpired(): void {
    this.clear('expired');
  }

  dispose(): void {
    this.cancelTimer();
    if (this.channel) {
      this.channel.onmessage = null;
      this.channel.close();
    }
    this.listeners.clear();
  }

  // ---------------------------------------------------------------- internals
  private async runRefresh(rejected?: string | null): Promise<boolean> {
    const run = async (): Promise<boolean> => {
      // Another tab may have renewed while we waited for the lock: use its token instead of spending the cookie again.
      const alreadyRenewed =
        rejected === undefined
          ? this.token !== null && this.expiresAt - this.now() > this.leadMs + 5_000
          : this.token !== null && this.token !== rejected;
      if (alreadyRenewed) {
        return true;
      }
      let response: Response;
      try {
        response = await this.post('/api/v1/auth/refresh', undefined);
      } catch {
        this.scheduleRetry(); // offline: keep the session, try again soon
        return this.token !== null && this.expiresAt > this.now();
      }
      if (!response.ok) {
        this.clear(this.snapshot.status === 'authenticated' ? 'expired' : 'anonymous');
        return false;
      }
      this.adopt(await response.json(), true);
      return true;
    };
    return this.locks ? this.locks.request('adiox-refresh', run) : run();
  }

  private adopt(body: LoginBody, broadcast: boolean): void {
    this.token = body.accessToken;
    this.expiresAt = this.now() + body.expiresIn * 1000;
    this.setSnapshot({ status: 'authenticated', user: body.user });
    this.schedule();
    if (broadcast && this.channel) {
      const shared: Shared = { accessToken: this.token, expiresAt: this.expiresAt, user: body.user };
      this.channel.postMessage({ type: 'session', shared });
    }
  }

  private onBroadcast(data: unknown): void {
    const message = data as { type?: string; shared?: Shared } | null;
    if (message?.type === 'logout') {
      this.clear('anonymous', false);
      return;
    }
    const shared = message?.shared;
    if (message?.type === 'session' && shared && shared.expiresAt > this.expiresAt) {
      this.token = shared.accessToken;
      this.expiresAt = shared.expiresAt;
      this.setSnapshot({ status: 'authenticated', user: shared.user });
      this.schedule();
    }
  }

  private schedule(): void {
    this.cancelTimer();
    const delay = Math.max(1_000, this.expiresAt - this.now() - this.leadMs);
    this.timer = setTimeout(() => void this.refresh(), delay);
  }

  private scheduleRetry(): void {
    this.cancelTimer();
    this.timer = setTimeout(() => void this.refresh(), this.retryMs);
  }

  private cancelTimer(): void {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }

  private clear(status: SessionStatus, broadcast = true): void {
    this.cancelTimer();
    this.token = null;
    this.expiresAt = 0;
    this.setSnapshot({ status, user: null });
    if (broadcast && status === 'anonymous' && this.channel) {
      this.channel.postMessage({ type: 'logout' });
    }
  }

  private setSnapshot(next: SessionSnapshot): void {
    this.snapshot = next;
    this.listeners.forEach((listener) => listener(next));
  }

  private post(path: string, body: unknown): Promise<Response> {
    return this.fetchFn(`${this.baseUrl}${path}`, {
      method: 'POST',
      credentials: 'include',
      headers: body === undefined ? undefined : { 'content-type': 'application/json' },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  }
}

export async function toError(response: Response): Promise<ApiError> {
  let problem: Record<string, unknown> | undefined;
  try {
    problem = (await response.json()) as Record<string, unknown>;
  } catch {
    problem = undefined;
  }
  const detail = typeof problem?.detail === 'string' ? problem.detail : undefined;
  const title = typeof problem?.title === 'string' ? problem.title : undefined;
  return new ApiError(response.status, detail ?? title ?? `Request failed (${response.status})`, problem);
}
